/**
 * @fileOverview This file contains the implementation of the entries & exits datagrid for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez, Santiago Cano Herrera
 */

//#region imports
import { Box, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';

import { getToast } from '../../utils/toastConfig';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';
import InventoryToolbar from '../Inventory/InventoryToolbar';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDown, faUp } from '@fortawesome/pro-solid-svg-icons';
import { useTableLocale } from '../../hooks/useTableLocale';
//#endregion

export const MovementsTable = () => {
  const [movements, setMovements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const {
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  const locale = useTableLocale();

  // Columns
  const movementsColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'item_name',
      headerName: 'Refacción',
      width: 150,
    },
    {
      field: 'movement_type_name',
      headerName: 'Movimiento',
      width: 100,
    },
    {
      field: 'io_type',
      headerName: 'Entrada/Salida',
      width: 100,
      renderCell: (params) => {
        if (params.value === 'OUTPUT') {
          return (
            <Box display="flex" justifyContent="center" width="100%">
              <FontAwesomeIcon icon={faDown} size="lg" color="red" />
            </Box>
          );
        } else {
          return (
            <Box display="flex" justifyContent="center" width="100%">
              <FontAwesomeIcon icon={faUp} size="lg" color="green" />
            </Box>
          );
        }
      },
    },
    {
      field: 'movement_date',
      headerName: 'Fecha',
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        const day = date.toLocaleDateString('es-MX', { day: '2-digit' });
        const month = date.toLocaleDateString('es-MX', { month: 'short' });
        const year = date.toLocaleDateString('es-MX', { year: 'numeric' });

        const formattedDate = `${day} de ${month} de ${year}`;

        return formattedDate;
      },
    },
    {
      field: 'delta_amount',
      headerName: 'Cantidad',
      width: 100,
      renderCell: (params) => {
        return (
          <div
            style={{
              width: '100%',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: 'unit_name',
      headerName: 'Unidad',
      width: 100,
    },
    {
      field: 'rack',
      headerName: 'Ubicación',
      width: 100,
    },
    {
      field: 'inventory_name',
      headerName: 'Almacén',
      width: 100,
    },
    {
      field: 'supervisor_name',
      headerName: 'Supervisor',
      width: 100,
    },
    {
      field: 'reason',
      headerName: 'Razón',
      width: 100,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'created_by',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    {
      field: 'updated_by',
      headerName: updatedByString,
      width: 150,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: 200,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  const fetchMovements = async () => {
    const endpoint = `${GlobalVariables.getURL()}movements`;
    setIsLoading(true);
    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.status === 200) {
        setMovements(response.data.data);
      }
    } catch (error) {
      console.log(error);
      getToast({
        status: 'error',
        title: 'Hubo un error al cargar los movimientos',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
  }, []);

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">Movimientos</span>
        </div>
      </div>

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        pagination
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: { right: ['action'] },
        }}
        rows={movements}
        columns={movementsColumns /* .concat(actionColumn) */}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
          if (newRowSelectionModel.length >= 1) {
            setIsButtonDisabled(false);
          }
          if (newRowSelectionModel.length === 0) {
            setIsButtonDisabled(true);
          }
        }}
        rowSelectionModel={rowSelectionModel}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay movimientos del inventario
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
        className="equips-datagrid-tour"
      />
    </>
  );
};
